import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  TextNode,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { createPortal } from 'react-dom';
import {
  UserSuggestion,
  useUserSuggestions,
} from '../../common/use-user-suggestions';
import { checkForAtSignMentions } from '../mention-shared/at-sign-matcher';
import { $handleMentionBackspace, $insertMention } from './mention-commands';
import { useHistory } from 'react-router';
import { Link } from '../ui';
import { Icon } from '@socialchorus/shared-ui-components';
import DEFAULT_AVATAR_NEW from '../../models/advocate/avatar';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

class MentionTypeAheadOption extends MenuOption {
  id: string;
  name: string;
  avatar: UserSuggestion['avatar'];
  displayAttributes: string[];

  constructor(suggestion: UserSuggestion) {
    super(suggestion.name);
    this.id = String(suggestion.id);
    this.name = suggestion.name;
    this.avatar = suggestion.avatar;
    this.displayAttributes = suggestion.display_attributes;
  }
}

function MentionMenuItem({
  option,
  isSelected,
  onClick,
  onMouseEnter,
}: {
  option: MentionTypeAheadOption;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  // Inspired by InternalSuggestionPortal.tsx in user-tag-plugin
  const history = useHistory();
  const profileUrl = history.createHref({ pathname: `/profiles/${option.id}` });

  return (
    <div
      className={`lexical-mention-entry${
        isSelected ? ' lexical-mention-entry--active' : ''
      }`}
      role="option"
      aria-selected={isSelected ? true : undefined}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      onMouseEnter={onMouseEnter}
      ref={(el) => {
        if (isSelected && el) {
          el.scrollIntoView({ block: 'nearest' });
        }
      }}
    >
      <div className="lexical-mention-avatar">
        <img
          src={option.avatar.url || DEFAULT_AVATAR_NEW}
          alt={option.name}
          width={40}
          height={40}
        />
      </div>
      <div className="lexical-mention-entry-text">
        <div className="lexical-mention-name">{option.name}</div>
        {option.displayAttributes.length > 0 && (
          <div className="lexical-mention-details">
            {option.displayAttributes.join(' \u2022 ')}
          </div>
        )}
      </div>
      <Link
        href={profileUrl}
        target="_blank"
        className={'lexical-mention-profile-link'}
      >
        <Icon size={24}>open_in_new</Icon>
      </Link>
    </div>
  );
}

const MentionPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string>('');
  const suggestions = useUserSuggestions(queryString);
  // `LexicalTypeaheadMenuPlugin` only ever closes its menu from an editor
  // update listener. Moving focus to another field (the UGC Title input, say)
  // is not an editor update, so the dropdown would otherwise stay open over a
  // field the user has already left. Hide it from blur until the plugin next
  // recomputes the trigger against a live selection — which is exactly when
  // `onQueryChange` fires, so the menu never reappears at a stale position.
  const [suppressedByBlur, setSuppressedByBlur] = useState(false);

  const handleQueryChange = useCallback((matchingString: string | null) => {
    setSuppressedByBlur(false);
    // Trim so multi-word queries search on their content (e.g. "Jo " -> "Jo")
    // rather than sending trailing/leading spaces to the user search.
    setQueryString(matchingString?.trim() ?? '');
  }, []);

  const users = useMemo(
    () => suggestions.map((s) => new MentionTypeAheadOption(s)),
    [suggestions]
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeAheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        $insertMention(
          nodeToReplace,
          selectedOption.id,
          selectedOption.name,
          'user'
        );
        closeMenu();
      });
    },
    [editor]
  );

  useEffect(
    () =>
      mergeRegister(
        // Backspacing the auto-inserted space right after a mention removes both
        // the space and the whole token (spec #18); returning true prevents the
        // default single-character delete.
        editor.registerCommand(
          KEY_BACKSPACE_COMMAND,
          () => $handleMentionBackspace(),
          COMMAND_PRIORITY_LOW
        ),
        // Focus leaving the editor ends the active query. Return false so this
        // stays a passive observer of the blur.
        editor.registerCommand(
          BLUR_COMMAND,
          () => {
            setSuppressedByBlur(true);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor]
  );

  const checkForMentionMatch = useCallback((text: string) => {
    return checkForAtSignMentions(text, 1);
  }, []);

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeAheadOption>
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={users}
      anchorClassName="lexical-mention-anchor"
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (
          !anchorElementRef.current ||
          users.length === 0 ||
          suppressedByBlur
        ) {
          return null;
        }

        return createPortal(
          // The menu is portaled outside the contenteditable, so clicking an
          // option would blur the editor and close the menu before `onClick`
          // fires. Suppressing the mousedown default keeps focus in the editor
          // (mirrors the plain reference-text-input dropdown).
          <div
            className="lexical-mention-dropdown"
            role="listbox"
            aria-label="User suggestions"
            // Not tab-reachable: the editor keeps focus and drives the menu
            // with arrow keys, so the listbox is only ever focused
            // programmatically.
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
          >
            {users.map((user, i) => (
              <MentionMenuItem
                key={user.id}
                option={user}
                isSelected={selectedIndex === i}
                onClick={() => {
                  setHighlightedIndex(i);
                  selectOptionAndCleanUp(user);
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
              />
            ))}
          </div>,
          anchorElementRef.current
        );
      }}
    />
  );
};

export default MentionPlugin;
